import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from './Logo';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Magazine', to: '/magazine' },
  { label: 'Podcast', to: '/podcasts' },
  { label: 'ABC', to: '/abc' },
  { label: 'FELAS', to: '/felas' },
  { label: 'IECL', to: '/iecl' },
  { label: 'The100', to: '/the100' },
  { label: 'Club', href: 'https://oneenergy.club', external: true },
];

const DARK_ROUTES = ['/the100'];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const dark = scrolled;
  const onDark = !scrolled && DARK_ROUTES.includes(location.pathname);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          dark ? 'bg-[#ffffff]/90 backdrop-blur-md border-b border-[#e5e5e5]' : 'bg-transparent'
        }`}
      >
        <div className="section-pad flex items-center justify-between h-[104px]">
          <Link to="/" className="flex items-center gap-2 group">
            <Logo light={onDark} className="text-[2.4rem] md:text-[2.9rem]" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              item.external ? (
                <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className={`text-[0.88rem] font-medium link-underline hover:text-[#D01919] transition-colors ${onDark ? 'text-white/85' : 'text-[#111111]/80'}`}>{item.label}</a>
              ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`text-[0.88rem] font-medium link-underline transition-colors ${
                  location.pathname === item.to ? 'text-[#D01919]' : `${onDark ? 'text-white/85' : 'text-[#111111]/80'} hover:text-[#D01919]`
                }`}
              >
                {item.label}
              </Link>
              )
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className={`hidden sm:inline-flex items-center gap-1.5 ${onDark ? 'bg-white text-[#111111] hover:text-white' : 'bg-[#111111] text-[#ffffff]'} text-[0.9rem] font-medium px-5 py-2.5 rounded-full hover:bg-[#D01919] transition-colors duration-300`}
            >
              Subscribe <ArrowUpRight size={16} />
            </Link>
            <button
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full ${onDark ? 'bg-white text-[#111111]' : 'bg-[#111111] text-[#ffffff]'}`}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-all duration-500 ${
          open ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        <div className="absolute inset-0 bg-[#111111]/40" onClick={() => setOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[#111111] text-[#ffffff] p-8 flex flex-col transition-transform duration-500 ${
            open ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex justify-between items-center mb-12">
            <Logo light className="text-3xl" />
            <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-[#D01919]">
              <X size={26} />
            </button>
          </div>
          <nav className="flex flex-col gap-2">
            {navItems.map((item, i) => (
              item.external ? (
                <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className="font-display text-3xl py-3 border-b border-white/10 hover:text-[#D01919] transition-colors">{item.label}</a>
              ) : (
              <Link
                key={item.to}
                to={item.to}
                className="font-display text-3xl py-3 border-b border-white/10 hover:text-[#D01919] transition-colors"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {item.label}
              </Link>
              )
            ))}
          </nav>
          <Link
            to="/contact"
            className="mt-auto inline-flex items-center justify-center gap-1.5 bg-[#D01919] text-white font-semibold px-5 py-3.5 rounded-full"
          >
            Subscribe <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </>
  );
}
